import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../../classes/api";
import { ICartProduct, IExtra } from "../../../classes/interfaces";

interface UseFlavourExtras {
  extras: IExtra[] | null | undefined;
  loading: boolean;
  moreExtra: (extra: IExtra) => void;
  lessExtra: (extra: IExtra) => void;
}

/**
 * Extracted 1:1 from the old CollapsedProductExtra component (fetch-on-expand
 * + mutate-in-place extras logic), minus the useCollapse/JSX concerns — the
 * caller owns the `expanded` boolean and re-render triggering via `onChange`.
 */
export function useFlavourExtras(
  flavour: ICartProduct | null | undefined,
  // amountFlavors is consumed by callers to divide the displayed unit price
  // (extra.price / amountFlavors), matching the original component's display.
  amountFlavors: number,
  expanded: boolean,
  onChange: () => void
): UseFlavourExtras {
  const [loading, setLoading] = useState(false);
  const [extras, setExtras] = useState<IExtra[] | null | undefined>(undefined);

  useEffect(() => {
    if (expanded && (!extras || extras === null)) {
      setLoading(true);
      api()
        .get(`/product-size/${flavour?.sizeId}/${flavour?.product_id}/extras`)
        .then((res: any) => {
          setLoading(false);
          if (res.data === undefined || res.data.code === undefined) {
            toast(res.data, { type: "error", autoClose: 3000 });
            return;
          }
          if (res.data.code !== 200) {
            toast(res.data, { type: "error", autoClose: 3000 });
            return;
          }
          if (res.data.data) {
            setExtras(res.data.data as IExtra[]);
          }
        })
        .catch((error) => {
          setLoading(false);
          toast(error, { type: "error", autoClose: 3000 });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mirrors CollapsedProductExtra's original effect: fetch once per expand, guarded by extras/expanded
  }, [expanded]);

  function moreExtra(extra: IExtra) {
    if (extra.max > 0 && extra.amount >= extra.max) {
      toast("Não é permitido mais desse item", { type: "error", autoClose: 3000 });
      return;
    }

    extra.amount += 1;

    let searched = false;
    flavour?.extras.forEach((item) => {
      if (item.id === extra.id) {
        searched = true;
        item.amount = extra.amount;
      }
    });

    if (!searched) {
      flavour?.extras.push(extra);
    }

    onChange();
  }

  function lessExtra(extra: IExtra) {
    if (extra.amount <= 0) {
      return;
    }

    extra.amount -= 1;

    if (extra.amount <= 0) {
      const newExtras = flavour?.extras.filter((item) => item.id !== extra.id);
      if (flavour) {
        flavour.extras = newExtras || flavour.extras;
      }
    }

    onChange();
  }

  return { extras, loading, moreExtra, lessExtra };
}
